import { inject, injectable } from 'inversify';
import { RpcSuccess, RpcError } from '@dealroadshow/json-rpc-dispatcher';
import { v4 as uuid } from 'uuid';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import { ICreatePortfolioPayload } from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioPayload';
import {
  IGetBondsByCusipsPayload,
  IGetBondsByTrancheIdsPayload,
  IGetUnknownBondOptionsPayload,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/IBondsPayload';
import {
  IPortfolioItem,
  IPortfolioItemPure,
  IPortfolioWithoutListsResponse,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/IPortfoliosCollection';
import {
  IDeletePositionsPayload,
  IPositionsPayload,
  IPositionsResponse,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/IPositionsCollection';
import { SortOrder } from '@creditflowresearch/uilib/Table/DataTable/interfaces/SortOrder';
import {
  IGetBondsByCusipsResponse,
  IGetBondsByTrancheIdsResponse,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioResponse';
import { type ISocketClient } from '@finsight/socket-client';
import SocketDITypes from '@finsight/socket-client/DITypes';
import { Subscription } from '@dealroadshow/socket-frontend-sdk';

@injectable()
class PortfolioMonitorRepository {
  private deletePositionsSubscription: Subscription | null = null;

  private savePortfolioSubscription: Subscription | null = null;

  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
    @inject(SocketDITypes.SocketClient) protected readonly socketPush: ISocketClient,
  ) {}

  getMarketPortfolios = async (): Promise<IPortfolioItemPure[]> => {
    let request = new Request('finsight_web.market.cusip_monitor.get_portfolios', {});
    let response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getPortfolios = async (payload:
      { sortBy: string, sortOrder: SortOrder },
  ): Promise<IPortfolioWithoutListsResponse> => {
    const request = new Request('finsight_web.cusip_monitor.portfolio.get_portfolios', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getPortfolioById = async (payload: { id: string }): Promise<IPortfolioItem> => {
    const request = new Request('finsight_web.cusip_monitor.portfolio.get_portfolio_with_lists_by_id', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  deletePortfolio = async (portfolioId: string) => {
    const request = new Request('finsight_web.cusip_monitor.portfolio.delete', { id: portfolioId });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  managePortfolios = async (portfolios: IPortfolioItem[]) => {
    const request = new Request('finsight_web.cusip_monitor.portfolio.manage', { portfolios });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getUnknownBondOptions = async (payload: IGetUnknownBondOptionsPayload) => {
    const request = new Request('finsight_web.cusip_monitor.bonds.search', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  savePortfolio = async (payload: ICreatePortfolioPayload): Promise<boolean> => {
    const entityId = uuid();

    const request = new Request('finsight_web.cusip_monitor.portfolio.save', {
      ...payload,
      entityId,
    });

    const socketSubscribeReq = new Request('finsight_web.socket.notifications', {
      entityId,
    });

    this.savePortfolioSubscription = await this.socketPush.subscribe(socketSubscribeReq);
    await this.rpc.call<RpcSuccess>(request);

    return new Promise((resolve, reject) => {
      this.savePortfolioSubscription.on('finsight_web.action_complete',
          ({
            params: {
              payload: {
                status,
                data: { error },
              },
            },
          }) => {
            this.savePortfolioSubscription.cancel();
            if (status === 'SUCCESS') {
              resolve(true);
            } else {
              reject(new RpcError({
                id: request.id,
                error,
              }));
            }
          },
      );
    });
  };

  getBondsByCusips = async (payload: IGetBondsByCusipsPayload): Promise<IGetBondsByCusipsResponse> => {
    const request = new Request('finsight_web.cusip_monitor.bonds.get_by_cusips', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getBondsByTrancheIds = async (payload: IGetBondsByTrancheIdsPayload): Promise<IGetBondsByTrancheIdsResponse> => {
    const request = new Request('finsight_web.cusip_monitor.bonds.get_by_tranche_ids', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  deletePositions = async (payload: IDeletePositionsPayload): Promise<boolean> => {
    const entityId = uuid();

    const request = new Request('finsight_web.cusip_monitor.portfolio.list.position.bulk_delete', {
      ...payload,
      entityId,
    });

    const socketSubscribeReq = new Request('finsight_web.socket.notifications', {
      entityId,
    });

    this.deletePositionsSubscription = await this.socketPush.subscribe(socketSubscribeReq);
    await this.rpc.call<RpcSuccess>(request);

    return new Promise((resolve, reject) => {
      this.deletePositionsSubscription.on('finsight_web.action_complete',
          ({
            params: {
              payload: {
                status,
                data: { error },
              },
            },
          }) => {
            this.deletePositionsSubscription.cancel();
            if (status === 'SUCCESS') {
              resolve(true);
            } else {
              reject(new RpcError({
                id: request.id,
                error,
              }));
            }
          },
      );
    });
  };

  getPositionsByList = async (payload: IPositionsPayload): Promise<IPositionsResponse> => {
    const request = new Request('finsight_web.market.cusip_monitor.get_positions_by_list_id', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };
}

export default PortfolioMonitorRepository;
